import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const DialogContext = createContext();

export function DialogProvider({ children }) {
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [alertDialog, setAlertDialog] = useState(null);
  const [promptDialog, setPromptDialog] = useState(null);
  const [promptValue, setPromptValue] = useState('');
  const promptInputRef = useRef(null);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        title: options.title || 'Confirm Action',
        description: options.description || 'Are you sure?',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        variant: options.variant || 'default', // 'default' or 'destructive'
        onConfirm: () => {
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmDialog(null);
          resolve(false);
        },
      });
    });
  }, []);

  const alert = useCallback((options) => {
    return new Promise((resolve) => {
      setAlertDialog({
        title: options.title || 'Alert',
        description: options.description || '',
        confirmText: options.confirmText || 'OK',
        variant: options.variant || 'default',
        onConfirm: () => {
          setAlertDialog(null);
          resolve(true);
        },
      });
    });
  }, []);

  const prompt = useCallback((options) => {
    return new Promise((resolve) => {
      setPromptValue(options?.defaultValue || '');
      setPromptDialog({
        title: options?.title || 'Enter value',
        description: options?.description || '',
        placeholder: options?.placeholder || '',
        confirmText: options?.confirmText || 'OK',
        cancelText: options?.cancelText || 'Cancel',
        multiline: !!options?.multiline,
        rows: options?.rows || 3,
        inputType: options?.inputType || 'text',
        required: !!options?.required,
        onConfirm: (value) => {
          setPromptDialog(null);
          resolve(value);
        },
        onCancel: () => {
          setPromptDialog(null);
          resolve(null);
        },
      });
    });
  }, []);

  // Autofocus the prompt input when it opens
  useEffect(() => {
    if (promptDialog) {
      const t = setTimeout(() => {
        promptInputRef.current?.focus();
        promptInputRef.current?.select?.();
      }, 60);
      return () => clearTimeout(t);
    }
  }, [promptDialog]);

  const handlePromptSubmit = (e) => {
    e?.preventDefault?.();
    if (promptDialog?.required && !promptValue?.trim()) return;
    promptDialog?.onConfirm(promptValue);
  };

  return (
    <DialogContext.Provider value={{ confirm, alert, prompt }}>
      {children}

      {/* Confirmation Dialog */}
      <AlertDialog open={!!confirmDialog} onOpenChange={(open) => !open && confirmDialog?.onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDialog?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={confirmDialog?.onCancel} data-testid="dialog-cancel-button">
              {confirmDialog?.cancelText}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog?.onConfirm}
              data-testid="dialog-confirm-button"
              className={confirmDialog?.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {confirmDialog?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alert Dialog */}
      <AlertDialog open={!!alertDialog} onOpenChange={(open) => !open && alertDialog?.onConfirm()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{alertDialog?.title}</AlertDialogTitle>
            <AlertDialogDescription>{alertDialog?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={alertDialog?.onConfirm}
              data-testid="dialog-alert-ok-button"
              className={alertDialog?.variant === 'destructive' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {alertDialog?.confirmText}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Prompt Dialog */}
      <Dialog open={!!promptDialog} onOpenChange={(open) => !open && promptDialog?.onCancel()}>
        <DialogContent className="sm:max-w-md" data-testid="dialog-prompt">
          <form onSubmit={handlePromptSubmit}>
            <DialogHeader>
              <DialogTitle>{promptDialog?.title}</DialogTitle>
              {promptDialog?.description && (
                <DialogDescription>{promptDialog.description}</DialogDescription>
              )}
            </DialogHeader>
            <div className="py-4">
              {promptDialog?.multiline ? (
                <textarea
                  ref={promptInputRef}
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  placeholder={promptDialog?.placeholder}
                  rows={promptDialog?.rows}
                  data-testid="dialog-prompt-input"
                  className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              ) : (
                <input
                  ref={promptInputRef}
                  type={promptDialog?.inputType}
                  value={promptValue}
                  onChange={(e) => setPromptValue(e.target.value)}
                  placeholder={promptDialog?.placeholder}
                  data-testid="dialog-prompt-input"
                  className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                />
              )}
            </div>
            <DialogFooter className="gap-2">
              <button
                type="button"
                onClick={promptDialog?.onCancel}
                data-testid="dialog-prompt-cancel"
                className="border border-border px-4 py-2 text-sm hover:bg-muted"
              >
                {promptDialog?.cancelText}
              </button>
              <button
                type="submit"
                data-testid="dialog-prompt-confirm"
                className="btn-primary px-4 py-2 text-sm"
              >
                {promptDialog?.confirmText}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialog must be used within DialogProvider');
  }
  return context;
}
